import i18n from "@rmp/core/plugins/i18n/core";
import { Temporal } from "temporal-polyfill";

declare const calendarDateBrand: unique symbol;

export type CalendarDate = string & { readonly [calendarDateBrand]: true };

const ISO_CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DISPLAY_CALENDAR_DATE = /^(\d{2})\.(\d{2})\.(\d{4})$/;
const RUSSIAN_MONTHS_GENITIVE = [
	"января", "февраля", "марта", "апреля", "мая", "июня",
	"июля", "августа", "сентября", "октября", "ноября", "декабря"
];

export const isCalendarDate = (value: unknown): value is CalendarDate => {
	if(typeof value !== "string" || !ISO_CALENDAR_DATE.test(value)) return false;
	try {
		return Temporal.PlainDate.from(value).year >= 1;
	} catch {
		return false;
	}
};

export const toCalendarDate = (value: string): CalendarDate => {
	if(!isCalendarDate(value)) throw new RangeError(i18n.t("errors.invalidCalendarDate", { value }).toString());
	return value;
};

export const calendarDateFromDisplay = (value: string): CalendarDate | null => {
	const match = DISPLAY_CALENDAR_DATE.exec(value);
	if(!match) return null;

	const [, day, month, year] = match;
	const result = `${year}-${month}-${day}`;
	return isCalendarDate(result) ? result : null;
};

export const formatCalendarDate = (value: string): string => {
	const [year, month, day] = toCalendarDate(value).split("-");
	return `${day}.${month}.${year}`;
};

export const formatFriendlyCalendarDate = (value: string): string => {
	const [year, month, day] = toCalendarDate(value).split("-");
	return `${day} ${RUSSIAN_MONTHS_GENITIVE[Number(month) - 1]} ${year}`;
};

export const calendarDateFromIsoDateTime = (value: string): CalendarDate =>
	toCalendarDate(value.slice(0, 10));

export const resolveCalendarDateStartToUtcIso = (
	value: CalendarDate,
	timeZone: string
): string => Temporal.PlainDate.from(value)
	.toPlainDateTime()
	.toZonedDateTime(timeZone, { disambiguation: "reject" })
	.toInstant()
	.toString({ smallestUnit: "millisecond" });

export const resolveCalendarDateEndToUtcIso = (
	value: CalendarDate,
	timeZone: string
): string => Temporal.PlainDate.from(value)
	.add({ days: 1 })
	.toPlainDateTime()
	.toZonedDateTime(timeZone, { disambiguation: "reject" })
	.toInstant()
	.subtract({ milliseconds: 1 })
	.toString({ smallestUnit: "millisecond" });

export const getCurrentCalendarDate = (): CalendarDate =>
	toCalendarDate(Temporal.Now.plainDateISO().toString());

export const getCurrentCalendarDateInTimeZone = (timeZone: string): CalendarDate =>
	toCalendarDate(Temporal.Now.plainDateISO(timeZone).toString());

export const getNextCalendarDate = (value: CalendarDate): CalendarDate =>
	toCalendarDate(Temporal.PlainDate.from(value).add({ days: 1 }).toString());

export const getPreviousCalendarDate = (value: CalendarDate): CalendarDate =>
	toCalendarDate(Temporal.PlainDate.from(value).subtract({ days: 1 }).toString());

export const getTomorrowCalendarDate = (): CalendarDate =>
	getNextCalendarDate(getCurrentCalendarDate());

export const isCalendarDateExpired = (
	expirationDate: CalendarDate | null | undefined,
	currentDate: CalendarDate
): boolean => expirationDate != null && expirationDate < currentDate;

export const getYearAheadUtcIsoRange = (timeZone: string): { from: string; till: string } => {
	const from = getCurrentCalendarDateInTimeZone(timeZone);
	const till = toCalendarDate(Temporal.PlainDate.from(from).add({ years: 1 }).toString());
	return {
		from: resolveCalendarDateStartToUtcIso(from, timeZone),
		till: resolveCalendarDateEndToUtcIso(till, timeZone)
	};
};
